import COLORS from 'constants/colors';
import { COURSE_STATE } from 'constants/student-courses';

import { format } from 'date-fns';

export const getRandomColor = () => {
  const RANDOM_COLORS = [
    COLORS.HEART_LIGHT,
    COLORS.SKY_DARK,
    COLORS.HEART_LIGHT,
    COLORS.GRASS,
    COLORS.HEART_DARK,
    COLORS.PRIMARY,
    COLORS.SUN,
    COLORS.SKY,
    COLORS.FLOWER_DARK,
    COLORS.GRASS_XLIGHT,
    COLORS.ORANGE,
    COLORS.ORANGE,
    COLORS.SUN,
    COLORS.GRASS,
    COLORS.HEART_XDARK,
  ];

  return RANDOM_COLORS[Math.floor(Math.random() * RANDOM_COLORS.length)];
};

const getMidnight = day => {
  const date = new Date(day);

  date.setMilliseconds(999);
  date.setSeconds(59);
  date.setMinutes(59);
  date.setHours(23);

  return date;
};

const isTomorrow = date => {
  const oneDay = 1000 * 60 * 60 * 24;
  const midnightTonight = getMidnight(new Date());
  const midnightTomorrow = new Date(midnightTonight.getTime() + oneDay);

  return date > midnightTonight && date < midnightTomorrow;
};

export const getDay = ptmSchedulesDateTime => {
  const date = format(ptmSchedulesDateTime, 'dd MMM yyyy');
  const isToday = new Date().toDateString() === ptmSchedulesDateTime.toDateString();

  if (isToday) {
    return 'today';
  }

  if (isTomorrow(ptmSchedulesDateTime)) {
    return 'tomorrow';
  }

  return date;
};

export function getCookie(name) {
  const cookies = document.cookie.split('; ');

  for (let i = 0; i < cookies.length; i += 1) {
    const [key, value] = cookies[i].split('=');

    if (key === name) {
      return value;
    }
  }

  return null;
}

export const scrollToItem = (item, startPosition) => {
  const diff = item.offsetTop - window.scrollY;

  if (startPosition === window.scrollY) {
    clearTimeout(window.TO);

    return;
  }

  if (Math.abs(diff) > 10) {
    const initialPosition = window.scrollY;

    window.scrollTo(0, window.scrollY + 5);
    window.TO = setTimeout(scrollToItem, 1, item, initialPosition);
  } else {
    window.scrollTo(0, item.offsetTop);
    clearTimeout(window.TO);
  }
};

const TARGET_MET = [
  '"Great Job! Keep it up!"',
  '"Superb! Keep up the good work!"',
  '"Terrific! You’re learning a lot!"',
  '"Beautiful! You’re getting better and better!"',
];
const TARGET_MISSED = [
  '"Try again, a little bit harder this time. Relax, you got this!"',
  '"Keep going, you are almost there! Just a little more effort."',
  '"Focus up! High Accuracy = Better Rewards."',
  '"Made a few mistakes? That’s good! Mistakes help you learn."',
];

export const isFunction = functionToCheck => {
  return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
};

export const checkArrayNotEmpty = array => {
  return Array.isArray(array) && array.length > 0;
};

export const isScrollAProperty = (container, scrollBehavior = 'auto') => {
  let doesScroll = false;

  try {
    container.scroll({
      get behavior() {
        doesScroll = true;

        return scrollBehavior;
      },
    });
  } catch (err) {
    return false;
  }

  return doesScroll;
};

export function getFeedback(accuracy, targetAccuracy) {
  let messages;

  if (accuracy >= targetAccuracy) {
    messages = TARGET_MET;
  } else {
    messages = TARGET_MISSED;
  }

  return messages[Math.floor(Math.random() * messages.length)];
}

export const loadScript = (() => {
  const promises = {};

  return (url, id) => {
    if (promises[id]) {
      return promises[id];
    }

    promises[id] = new Promise((resolve, reject) => {
      const existingElement = document.querySelector(`#${id}`);

      const head = document.getElementsByTagName('head')[0];
      const script = document.createElement('script');

      script.type = 'text/javascript';
      script.id = id;
      script.src = url;
      script.onreadystatechange = resolve;
      script.onload = resolve;
      script.onerror = event => {
        reject(new Error(`Failed to load ${event.target.src}`));
      };
      if (existingElement) {
        existingElement.remove();
      }

      head.appendChild(script);
    });

    promises[id]
      .then(() => {
        promises[id] = null;
      })
      .catch(() => {
        promises[id] = null;
      });

    return promises[id];
  };
})();

export const daysDiff = (firstDate, secondDate) => {
  const oneDay = 24 * 60 * 60 * 1000;

  return Math.round((secondDate - firstDate) / oneDay);
};

export const currentDateToEpoch = () => {
  const epochInSeconds = new Date().getTime() / 1000;

  return Math.floor(epochInSeconds);
};

export function dateObjToEpoch(string) {
  const date = new Date(string);

  date.setSeconds('0');
  date.setMilliseconds('0');
  const epochTime = date.getTime() / 1000;

  return epochTime;
}

export const checkAndGetExpiredCoursesMessages = studentCourses => {
  const expiresMessages = [];

  if (studentCourses && Array.isArray(studentCourses) && studentCourses.length > 0) {
    const currentDate = new Date();
    const options = { year: 'numeric', month: 'long', day: 'numeric' };

    studentCourses.forEach(course => {
      if (course.state === COURSE_STATE.INACT) {
        expiresMessages.push(`${course.course_name} is expired`);
      } else if (course.validity_till_date) {
        const expiryDate = new Date(course.validity_till_date * 1000);
        const currentDaysDiffrence = daysDiff(currentDate, expiryDate);

        if (currentDaysDiffrence < 14) {
          expiresMessages.push(
            `${course.course_name} is expiring on ${expiryDate.toLocaleDateString(
              undefined,
              options,
            )}`,
          );
        }
      }
    });
  }

  return expiresMessages;
};

export function capitalizeFirstLetter(string) {
  if (!string) {
    return '';
  }

  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const setAppType = () => {
  window.CUEMATH_APP_TYPE = 'LEAP';
};

export const setPolyfills = () => {
  Number.isFinite = Number.isFinite
    ? Number.isFinite
    : value => {
        return typeof value === 'number' && isFinite(value);
      };
};

export const getStringify = data => {
  return JSON.stringify(data);
};

export const getStudentWelcomeMessage = () => {
  const today = new Date();
  const timeNow = today.toLocaleTimeString();
  let greetingMessage;

  if (timeNow >= '00:00:00' && timeNow < '12:00:00') {
    greetingMessage = 'Good morning';
  } else if (timeNow >= '12:00:00' && timeNow < '17:00:00') {
    greetingMessage = 'Good afternoon';
  } else if (timeNow >= '17:00:00' && timeNow < '24:00:00') {
    greetingMessage = 'Good evening';
  } else {
    greetingMessage = 'Hello,';
  }

  return greetingMessage;
};

export const hasObjectGotAnyData = data => {
  return Object.keys(data || {}).length;
};

export const hasArrayGotAnyData = data => {
  return Array.isArray(data || []) && data.length > 0;
};

export const setDataToLocalStorage = (storageKey, storageValue) => {
  if (!storageKey) return null;

  return localStorage.setItem(storageKey, JSON.stringify(storageValue));
};

export const getDataFromLocalStorage = storageKey => {
  if (!storageKey) return null;

  return JSON.parse(localStorage.getItem(storageKey) || `{}`);
};

export const removeDataFromLocalStorage = storageKey => {
  return localStorage.removeItem(storageKey);
};

export const getFullName = (firstName, lastName) =>
  `${firstName || ''} ${lastName || ''}`.trim();

export const formatTimestamp = (timestampInSeconds, outputFormat = 'DD MMM YYYY') => {
  if (!timestampInSeconds) {
    return '';
  }

  const timestampInMilliseconds = timestampInSeconds * 1000;
  const formattedDate = format(timestampInMilliseconds, outputFormat);

  return formattedDate;
};
